import { CompoundKey } from "../../../utils/typedKeys";
import { ZERO_UUID_STRING } from "../../../utils/uuid";
import { UsageMap, DOOR_USAGE_CODES } from "../../../types/monitoringTypes";

/**
 * Diagnostic for the keying asymmetry between the producer and the consumer of
 * per-lift socket state.
 *
 * `liftStateRef` accumulates state under `compoundKey(bankId, liftId)`, but the
 * UI reads it back through `byLift[liftKey(liftId)]` — keyed by lift alone. A
 * message that arrives for an already-known lift under a *different* bankId
 * therefore misses the accumulator, gets a fresh `createDefaultState()` with an
 * empty usage map, and publishes that as the lift's current state. Every
 * accumulated door signal is discarded in one step, per lift.
 *
 * On this branch that shows up as `FloorCellIcon` falling back to the floor
 * index number in place of the door icon (`usageHasDoorData` goes false), with
 * the direction arrow still drawing from `useDirectionDebounce`'s remembered
 * value — visible, unlike the silently blank cell the pre-`FloorCellIcon`
 * components produced, but still wrong.
 *
 * Observation only: this mutates nothing but its own bookkeeping map, and
 * returns the warnings for the caller to emit rather than logging itself.
 * Callers gate it on `isBankIdTraceEnabled()` so it costs nothing unless the
 * `?traceBankId` query parameter is present.
 */

/**
 * The parts of a per-lift state object this diagnostic reads. Both the current
 * and the legacy state shapes satisfy it: the current one carries a populated
 * `usage`, the legacy one may carry only `bitmask`.
 */
export interface TrackedLiftState {
  usage?: UsageMap;
  bitmask?: Record<string, boolean>;
}

interface SignalCounts {
  total: number;
  door: number;
}

/** Count the signals a bucket had accumulated, from whichever map it carries. */
function countSignals(state: TrackedLiftState | undefined): SignalCounts {
  if (!state) return { total: 0, door: 0 };

  const { usage } = state;
  if (usage) {
    return {
      total: Object.keys(usage).length,
      door: DOOR_USAGE_CODES.filter((code) => code in usage).length,
    };
  }

  // Legacy shape: string keys of the form `bankId:liftId:Usage:FrontDoorOpened`.
  const keys = Object.keys(state.bitmask ?? {});
  return {
    total: keys.length,
    door: keys.filter((key) => key.includes(":Usage:") && key.includes("Door")).length,
  };
}

/** Extract the bank UUID from a `bankId:liftId` compound key. */
function bankIdOf(key: CompoundKey): string {
  return key.split(":")[0] ?? "";
}

/** Query parameter that turns the trace on, e.g. `/v2/monitoring?traceBankId`. */
export const TRACE_BANK_ID_PARAM = "traceBankId";

/**
 * Whether the bank-bucket trace is switched on for this page load.
 *
 * Read once per socket component at mount, never on the message path: with a
 * few dozen cars reporting continuously, re-parsing the query string per
 * message would be pure overhead. A query parameter needs a reload to change
 * anyway, so reading it once loses nothing.
 */
export function isBankIdTraceEnabled(search: string = window.location.search): boolean {
  return new URLSearchParams(search).has(TRACE_BANK_ID_PARAM);
}

/**
 * Records which accumulator bucket this lift is landing in and returns a
 * warning for every keying anomaly the message exposes. Returns an empty array
 * on the normal path.
 */
export default function trackLiftBucket(
  liftIdStr: string,
  bankIdStr: string,
  stateKey: CompoundKey,
  priorStateKeyByLift: Map<string, CompoundKey>,
  liftStates: ReadonlyMap<CompoundKey, TrackedLiftState>,
  source: string,
): string[] {
  const warnings: string[] = [];

  if (bankIdStr === ZERO_UUID_STRING) {
    warnings.push(
      `[WS][BANKID] ${source}: lift ${liftIdStr} reported with a ZERO bankId. ` +
        `Its state is accumulating in a bucket no other message can reach.`,
    );
  }

  const priorStateKey = priorStateKeyByLift.get(liftIdStr);
  priorStateKeyByLift.set(liftIdStr, stateKey);

  if (!priorStateKey || priorStateKey === stateKey) return warnings;

  const { total, door } = countSignals(liftStates.get(priorStateKey));

  warnings.push(
    `[WS][BANKID] ${source}: lift ${liftIdStr} switched bank bucket ` +
      `${bankIdOf(priorStateKey)} -> ${bankIdStr}. ` +
      `${total} accumulated signals (${door} door) are no longer reachable for ` +
      `this lift.`,
  );

  return warnings;
}
